import { useCallback, useEffect, useState } from 'react';
import { api, isAbort } from '../../shell/bridge';

/**
 * Interests questionnaire (card 0OvyJbpnACMX).
 *
 * The agent asks, the person answers — one question at a time, as a chat,
 * because that is where the card said it belongs and because four boxes in a
 * row read as a form nobody wants to fill in. Topics come from the agent when
 * the 'interests' permission is on, and from the person's own words when it is
 * not; the source is shown either way, and every chip is editable before the
 * profile is saved.
 *
 * Saved answers are read back on load, so a half-finished profile resumes at
 * the question it stopped on rather than starting again.
 */

interface Answers {
  weekend: string;
  music: string;
  teams: string;
  movies: string;
}

interface OwnInterests {
  memberId: string;
  answers: Answers;
  topics: string[];
  completed: boolean;
  updatedAt: number;
}

interface PartnerInterests {
  memberId: string;
  topics: string[];
  completed: boolean;
}

interface ExtractResult {
  topics: string[];
  source: 'model' | 'local';
  reason: string;
  allowed: boolean;
}

const QUESTIONS: { key: keyof Answers; prompt: string; hint: string }[] = [
  { key: 'weekend', prompt: 'What does a good weekend look like for you?', hint: 'Saturday coffee, a long run, the farmers market…' },
  { key: 'music', prompt: 'What are you listening to right now?', hint: 'Artists, albums, the playlist on repeat…' },
  { key: 'teams', prompt: 'Which teams do you follow?', hint: 'Codes, clubs, the group chat you check first…' },
  { key: 'movies', prompt: 'What do you watch when you want to switch off?', hint: 'Films, series, the comfort rewatch…' },
];

const EMPTY: Answers = { weekend: '', music: '', teams: '', movies: '' };
/** Mirrors INTEREST_ANSWER_MAX in shared/types.ts — the API rejects past it. */
const ANSWER_MAX = 300;
const TOPIC_MAX = 12;

export function Interests() {
  const [answers, setAnswers] = useState<Answers>(EMPTY);
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState('');
  const [topics, setTopics] = useState<string[]>([]);
  const [source, setSource] = useState('');
  const [partner, setPartner] = useState<PartnerInterests | null>(null);
  const [busy, setBusy] = useState<'load' | 'extract' | 'save' | null>('load');
  const [note, setNote] = useState('');
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api<{ interests: OwnInterests; partner: PartnerInterests | null }>('/api/interests');
      const rec = data.interests;
      if (rec && typeof rec.updatedAt === 'number' && rec.updatedAt > 0) {
        setAnswers({ ...EMPTY, ...rec.answers });
        setStep(QUESTIONS.length);
        setTopics(rec.topics ?? []);
        setSource('saved');
      }
      setPartner(data.partner ?? null);
      setFailed(false);
    } catch (err) {
      // A page teardown cancels this request; that is not a load failure and
      // must not paint Interests as broken (card d0Md65M0shSI).
      if (!isAbort(err)) {
        console.error('[Pear] interests load failed', err);
        setFailed(true);
      }
    } finally {
      setReady(true);
      setBusy(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const askNext = () => {
    const text = draft.trim();
    if (!text) return;
    const key = QUESTIONS[step].key;
    if (text.length > ANSWER_MAX) {
      setNote('That answer is too long — 300 characters max.');
      return;
    }
    setAnswers(prev => ({ ...prev, [key]: text }));
    setDraft('');
    setNote('');
    setStep(s => s + 1);
  };

  const skip = () => {
    const key = QUESTIONS[step].key;
    setAnswers(prev => ({ ...prev, [key]: '' }));
    setDraft('');
    setNote('');
    setStep(s => s + 1);
  };

  const build = async () => {
    setBusy('extract');
    setNote('');
    try {
      const res = await api<ExtractResult>('/api/interests/extract', { body: { answers } });
      setTopics(res.topics ?? []);
      setSource(res.source);
      setNote(
        res.source === 'model'
          ? 'Topics from the concierge.'
          : 'Topics made from your answers — switch on “Interests” in AI access to let the concierge word them.',
      );
    } catch (err) {
      console.error('[Pear] interests extract failed', err);
      setNote('Couldn’t build topics. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setBusy('save');
    setNote('');
    try {
      const data = await api<{ interests: OwnInterests }>('/api/interests', {
        method: 'PUT',
        body: { answers, topics },
      });
      setNote(data.interests?.completed ? 'Profile saved — you’re done.' : 'Saved as a draft — finish the four questions to complete it.');
    } catch (err) {
      console.error('[Pear] interests save failed', err);
      setNote('Couldn’t save that. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const addTopic = (raw: string) => {
    const clean = raw.trim();
    if (!clean || topics.length >= TOPIC_MAX) return;
    if (topics.some(t => t.toLowerCase() === clean.toLowerCase())) return;
    setTopics(prev => [...prev, clean.slice(0, 30)]);
  };

  const restart = () => {
    setAnswers(EMPTY);
    setTopics([]);
    setSource('');
    setStep(0);
    setDraft('');
    setNote('');
  };

  const allAsked = step >= QUESTIONS.length;
  const answered = QUESTIONS.slice(0, step);

  return (
    <div className="ix" id="interests-section">
      <p className="set-section-note">
        Your concierge asks four questions and keeps what you’re into — so news, gifts and
        conversation starters land on your interests, not guesses.
      </p>

      {failed && <p className="set-error">Couldn’t load your profile. Try again shortly.</p>}
      {!ready && !failed && <p className="set-loading" aria-busy="true">Loading…</p>}

      <div className="ix-thread" id="ix-thread">
        {answered.map(q => (
          <div className="ix-pair" key={q.key}>
            <div className="ix-bubble ix-agent">{q.prompt}</div>
            <div className="ix-bubble ix-me" data-question={q.key}>
              {answers[q.key].trim() || 'skipped'}
            </div>
          </div>
        ))}

        {!allAsked && ready && (
          <div className="ix-pair">
            <div className="ix-bubble ix-agent" id="ix-question">
              {QUESTIONS[step].prompt}
              <span className="ix-hint">{QUESTIONS[step].hint}</span>
            </div>
            <div className="ix-compose">
              <textarea
                id="ix-answer"
                className="ix-input"
                rows={2}
                maxLength={ANSWER_MAX}
                placeholder="Your answer…"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    askNext();
                  }
                }}
              />
              <div className="ix-actions">
                <button type="button" className="pear-btn ix-skip" id="ix-skip" onClick={skip}>
                  Skip
                </button>
                <button
                  type="button"
                  className="pear-btn ix-send"
                  id="ix-send"
                  disabled={!draft.trim() || busy !== null}
                  onClick={askNext}
                >
                  {step === QUESTIONS.length - 1 ? 'Answer' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {allAsked && ready && (
        <div className="ix-topics">
          {topics.length === 0 && (
            <button type="button" className="pear-btn ix-build" id="ix-build" disabled={busy !== null} onClick={() => void build()}>
              {busy === 'extract' ? 'Building…' : 'Build my topics'}
            </button>
          )}

          {topics.length > 0 && (
            <>
              <div className="ix-chips" id="ix-chips">
                {topics.map((t, i) => (
                  <span className="ix-chip" key={`${t}-${i}`}>
                    {t}
                    <button
                      type="button"
                      className="ix-chip-x"
                      aria-label={`Remove ${t}`}
                      onClick={() => setTopics(prev => prev.filter((_, idx) => idx !== i))}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>

              <div className="ix-add">
                <input
                  id="ix-topic-input"
                  className="ix-input"
                  placeholder="Add a topic"
                  maxLength={30}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const el = e.currentTarget;
                      addTopic(el.value);
                      el.value = '';
                    }
                  }}
                />
                <button
                  type="button"
                  className="pear-btn"
                  id="ix-topic-add"
                  onClick={() => {
                    const el = document.getElementById('ix-topic-input') as HTMLInputElement | null;
                    if (!el) return;
                    addTopic(el.value);
                    el.value = '';
                  }}
                >
                  Add
                </button>
              </div>

              <div className="ix-actions">
                <button type="button" className="pear-btn ix-rebuild" id="ix-rebuild" disabled={busy !== null} onClick={() => void build()}>
                  Rebuild
                </button>
                <button type="button" className="pear-btn ix-save" id="ix-save" disabled={busy !== null} onClick={() => void save()}>
                  {busy === 'save' ? 'Saving…' : 'Save profile'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <p className="ix-note" id="ix-note" aria-live="polite">{note}</p>

      {allAsked && source === 'saved' && (
        <button type="button" className="ix-restart" id="ix-restart" onClick={restart}>
          Start the questions again
        </button>
      )}

      <div className="ix-partner" id="ix-partner">
        {partner === null ? (
          <span>Your partner hasn’t started theirs.</span>
        ) : partner.completed || partner.topics.length > 0 ? (
          <>
            <span>What they’re into:</span>
            <div className="ix-chips ix-chips-partner">
              {partner.topics.map((t, i) => (
                <span className="ix-chip" key={`${t}-${i}`}>{t}</span>
              ))}
              {partner.topics.length === 0 && <span className="ix-muted">no topics yet</span>}
            </div>
          </>
        ) : (
          <span>Your partner hasn’t filled theirs in yet.</span>
        )}
      </div>
    </div>
  );
}
